import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { adminPatientDetailApi } from "apis";
import type {
  AdditionalNotesResponseDto,
  EnvironmentToxinsResponseDto,
  FamilyHistoryResponseDto,
  FertilityReproductiveHealthResponseDto,
  FunctionalLifestyleResponseDto,
  GeneralInformationResponseDto,
  GoalsOfTreatmentResponseDto,
  MedicalHistoryResponseDto,
  MedicationStatementResponseDto,
  NutritionAndDietResponseDto,
  PresentingConcernsResponseDto,
  SocialHistoryResponseDto,
} from "libs/cellera-sdk";

interface UseAdminPatientSectionResult<T> {
  data: T;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const useAdminPatientSection = <T>(
  cacheKey: string,
  patientId: string,
  fetcher: (id: string) => Promise<T | null | undefined>,
  fallbackValue: T,
): UseAdminPatientSectionResult<T> => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [cacheKey, patientId],
    queryFn: async () => {
      if (!patientId) {
        return null;
      }

      return fetcher(patientId);
    },
    enabled: Boolean(patientId),
    staleTime: 5 * 60 * 1000,
  });

  const resolvedData = useMemo(() => {
    if (data === undefined || data === null) {
      return fallbackValue;
    }
    return data;
  }, [data, fallbackValue]);

  const resolvedError = useMemo(() => {
    if (!error) {
      return null;
    }
    return error instanceof Error ? error.message : String(error);
  }, [error]);

  return {
    data: resolvedData,
    loading: isLoading,
    error: resolvedError,
    refetch,
  };
};

const nullFactory = <T>(value: T | null = null): T | null => value;

export const useAdminPatientGeneralInformation = (
  patientId: string,
): UseAdminPatientSectionResult<GeneralInformationResponseDto | null> => {
  const fallback = useMemo(
    () => nullFactory<GeneralInformationResponseDto>(),
    [],
  );
  return useAdminPatientSection<GeneralInformationResponseDto | null>(
    "admin-patient-general-information",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientGeneralInformationForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientFunctionalLifestyle = (
  patientId: string,
): UseAdminPatientSectionResult<FunctionalLifestyleResponseDto | null> => {
  const fallback = useMemo(
    () => nullFactory<FunctionalLifestyleResponseDto>(),
    [],
  );
  return useAdminPatientSection<FunctionalLifestyleResponseDto | null>(
    "admin-patient-functional-lifestyle",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientFunctionalLifestyleForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientGoalsOfTreatment = (
  patientId: string,
): UseAdminPatientSectionResult<GoalsOfTreatmentResponseDto | null> => {
  const fallback = useMemo(
    () => nullFactory<GoalsOfTreatmentResponseDto>(),
    [],
  );
  return useAdminPatientSection<GoalsOfTreatmentResponseDto | null>(
    "admin-patient-goals-of-treatment",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientGoalsOfTreatmentForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientPresentingConcerns = (
  patientId: string,
): UseAdminPatientSectionResult<PresentingConcernsResponseDto | null> => {
  const fallback = useMemo(
    () => nullFactory<PresentingConcernsResponseDto>(),
    [],
  );
  return useAdminPatientSection<PresentingConcernsResponseDto | null>(
    "admin-patient-presenting-concerns",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientPresentingConcernsForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientNutrition = (
  patientId: string,
): UseAdminPatientSectionResult<NutritionAndDietResponseDto | null> => {
  const fallback = useMemo(
    () => nullFactory<NutritionAndDietResponseDto>(),
    [],
  );
  return useAdminPatientSection<NutritionAndDietResponseDto | null>(
    "admin-patient-nutrition",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientNutritionAndDietForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientEnvironmentToxins = (
  patientId: string,
): UseAdminPatientSectionResult<EnvironmentToxinsResponseDto | null> => {
  const fallback = useMemo(
    () => nullFactory<EnvironmentToxinsResponseDto>(),
    [],
  );
  return useAdminPatientSection<EnvironmentToxinsResponseDto | null>(
    "admin-patient-environment-toxins",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientEnvironmentToxinsForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientFamilyHistory = (
  patientId: string,
): UseAdminPatientSectionResult<FamilyHistoryResponseDto | null> => {
  const fallback = useMemo(() => nullFactory<FamilyHistoryResponseDto>(), []);
  return useAdminPatientSection<FamilyHistoryResponseDto | null>(
    "admin-patient-family-history",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientFamilyHistoryForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientFertilityReproductiveHealth = (
  patientId: string,
): UseAdminPatientSectionResult<FertilityReproductiveHealthResponseDto | null> => {
  const fallback = useMemo(
    () => nullFactory<FertilityReproductiveHealthResponseDto>(),
    [],
  );
  return useAdminPatientSection<FertilityReproductiveHealthResponseDto | null>(
    "admin-patient-fertility-reproductive-health",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientFertilityReproductiveHealthForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientSocialHistory = (
  patientId: string,
): UseAdminPatientSectionResult<SocialHistoryResponseDto | null> => {
  const fallback = useMemo(() => nullFactory<SocialHistoryResponseDto>(), []);
  return useAdminPatientSection<SocialHistoryResponseDto | null>(
    "admin-patient-social-history",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientSocialHistoryForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientAdditionalNotes = (
  patientId: string,
): UseAdminPatientSectionResult<AdditionalNotesResponseDto | null> => {
  const fallback = useMemo(() => nullFactory<AdditionalNotesResponseDto>(), []);
  return useAdminPatientSection<AdditionalNotesResponseDto | null>(
    "admin-patient-additional-notes",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientAdditionalNotesForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientMedicalHistory = (
  patientId: string,
): UseAdminPatientSectionResult<MedicalHistoryResponseDto | null> => {
  const fallback = useMemo(() => nullFactory<MedicalHistoryResponseDto>(), []);
  return useAdminPatientSection<MedicalHistoryResponseDto | null>(
    "admin-patient-medical-history",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientMedicalHistoryForAdmin({
        patientId: id,
      }),
    fallback,
  );
};

export const useAdminPatientMedications = (
  patientId: string,
): UseAdminPatientSectionResult<MedicationStatementResponseDto[]> => {
  const fallback = useMemo<MedicationStatementResponseDto[]>(() => [], []);

  const { data, loading, error, refetch } = useAdminPatientSection<
    MedicationStatementResponseDto[] | null
  >(
    "admin-patient-medications",
    patientId,
    (id) =>
      adminPatientDetailApi.getPatientMedicationsForAdmin({
        patientId: id,
      }),
    null,
  );

  const resolvedData = useMemo(() => data ?? fallback, [data, fallback]);

  return {
    data: resolvedData,
    loading,
    error,
    refetch,
  };
};
